import {Intent} from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import {queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useLocation} from 'react-router-dom';

import {usePushNotification} from 'app/components/Notification';
import {RECENT_RUNS_QUERY_KEY, usePendingAgentRuns} from 'app/modules/AgentRuns/usePendingAgentRuns';
import {api} from 'app/modules/Remote';
import {ApiNote} from 'app/modules/Remote/Feature';
import {useAuth} from 'app/providers/AuthProvider';
import {recordEvent} from 'app/tools/Analytics';
import {NOTE_TYPE_AGENT, NOTE_TYPE_USER, ORG_SETTING_AGENT_SETTINGS} from 'app/utils/constants';

import {ApiExploreRun, ApiExploreRunScene, ExploreRunPrompt} from './types';

export const exploreRunsQueryOptions = (featureId: number) =>
  queryOptions({
    queryKey: ['agent-explore-runs', featureId],
    queryFn: async () => {
      const response = await api.agent.getExploreRuns(featureId);
      // Keep every note associated with a run — including saved (user) and
      // dismissed (archived) notes — so they persist in the run list with a
      // status indicator instead of disappearing when acted on.
      return response.toJS().data as ApiExploreRun[];
    },
  });

export const useExploreRunsQuery = (featureId: number) => {
  return useQuery(exploreRunsQueryOptions(featureId));
};

export const useAgentRunCount = (organizationId: string) => {
  return useQuery({
    queryKey: ['agent-run-count', organizationId],
    queryFn: async () => {
      const response = await api.organizations.agentRunCount(organizationId);
      return response.toJS().data as number;
    },
  });
};

export const useUpdateAgentSettings = (organizationId: string) => {
  const auth = useAuth();
  const pushNotification = usePushNotification();
  return useMutation({
    mutationFn: (settings: {enabled?: boolean; hidden?: boolean}) =>
      api.agent.updateOrgAgentSettings(organizationId, settings),
    onSuccess: (response, settings) => {
      // agent_settings live on the organization, which is held in auth state (not react-query),
      // so we need to reflect the change there for useUserInfo() consumers to re-render.
      if (auth.status === 'logged in' && auth.currentOrganization) {
        const updatedAgentSettings = response.getIn([
          'data',
          'settings',
          ORG_SETTING_AGENT_SETTINGS,
        ]);
        auth.actions.setCurrentOrganization(
          auth.currentOrganization.setIn(
            ['settings', ORG_SETTING_AGENT_SETTINGS],
            updatedAgentSettings
          )
        );
      }
      recordEvent('Updated agent settings', {orgId: organizationId, ...settings});
    },
    onError: (error) => {
      Sentry.captureException(error);
      pushNotification({
        message: 'There was a problem updating your agent settings. Please try again.',
        autoHideDuration: 3000,
        options: {intent: Intent.DANGER},
      });
    },
  });
};

export const useStartExploreRun = (featureId: number, scenes: ApiExploreRunScene[]) => {
  const queryClient = useQueryClient();
  const {addPendingRun} = usePendingAgentRuns();
  const location = useLocation();
  const {queryKey} = exploreRunsQueryOptions(featureId);
  return useMutation({
    mutationFn: async (prompt: ExploreRunPrompt) => {
      const response = await api.agent.startExploreRun(featureId, scenes, prompt);
      return response.toJS().data as ApiExploreRun;
    },
    onSuccess: (newRun) => {
      // The current location is the feature's map page, with map state the lensUrl lacks.
      addPendingRun({runId: newRun.id, url: location.pathname + location.search});
      // Pull the new run into the polled recent-runs data.
      queryClient.invalidateQueries({queryKey: [RECENT_RUNS_QUERY_KEY]});
      queryClient.setQueryData(queryKey, (old: ApiExploreRun[] | undefined) =>
        old ? [...old, newRun] : [newRun]
      );
      queryClient.invalidateQueries({queryKey});
      // A new run consumes quota, so refresh the org's run count.
      queryClient.invalidateQueries({queryKey: ['agent-run-count']});
    },
  });
};

// The three states a person moves an observation between. Each is the whole
// note with its status fields changed, ready for useUpdateExploreNote.
export const saveNotePatch = (
  note: ApiNote,
  {text, userId}: {text: string; userId: string}
): ApiNote => ({
  ...note,
  text,
  userId,
  noteType: NOTE_TYPE_USER,
  // Saving also un-dismisses the note.
  isArchived: false,
});

// Reset to agent type on dismiss so a previously-saved note stays patchable (the
// notes API only allows updating archived agent notes), which keeps a later
// restore from 404ing.
export const dismissNotePatch = (note: ApiNote): ApiNote => ({
  ...note,
  isArchived: true,
  noteType: NOTE_TYPE_AGENT,
});

// Restoring is just flipping the note back to its un-acted-on state.
export const restoreNotePatch = (note: ApiNote): ApiNote => ({
  ...note,
  noteType: NOTE_TYPE_AGENT,
  isArchived: false,
});

// Patches a note in place, persisting whatever noteType/isArchived the caller
// sets. Used to save (noteType user, un-archived), dismiss (archived), and
// restore a note back to its un-acted-on agent state.
export const useUpdateExploreNote = (featureId: number) => {
  const queryClient = useQueryClient();
  const pushNotification = usePushNotification();
  const {queryKey} = exploreRunsQueryOptions(featureId);

  return useMutation({
    mutationFn: async (note: ApiNote) => {
      const result = await api.featureCollections
        .features(note.featureCollectionId)
        .patchNote(note.projectId, note.featureId, note.id as number, note);
      return result.toJS().data as ApiNote;
    },
    onMutate: async (note: ApiNote) => {
      await queryClient.cancelQueries({queryKey});
      const previousData = queryClient.getQueryData(queryKey);

      // Update the note in place (rather than removing it) so it stays in the
      // run list with its new status.
      queryClient.setQueryData(queryKey, (old: ApiExploreRun[] | undefined) => {
        if (!old) return old;
        return old.map((run) => ({
          ...run,
          notes: run.notes.map((n) => (n.id === note.id ? {...n, ...note} : n)),
        }));
      });

      return {previousData};
    },
    onSuccess: (_savedNote, note) => {
      // Only saving as a note gets a success toast; dismiss and restore stay quiet.
      if (note.noteType === NOTE_TYPE_USER && !note.isArchived) {
        pushNotification({
          message: 'Saved as note! Your observation is now available in the "Notes" tab.',
          autoHideDuration: 3000,
          options: {intent: Intent.SUCCESS, icon: 'tick-circle'},
        });
      }
    },
    onError: (_err, _note, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      pushNotification({
        message: 'There was a problem updating the note. Please try again.',
        autoHideDuration: 3000,
        options: {intent: Intent.DANGER},
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey});
    },
  });
};

// Merges several of a run's notes into one: the backend creates a new note
// whose geometry is the union of the sources' geometries and archives the
// sources, all in one request. The sources are archived optimistically; the
// merged note is added to its run when the response arrives.
export const useMergeExploreNotes = (featureId: number) => {
  const queryClient = useQueryClient();
  const {queryKey} = exploreRunsQueryOptions(featureId);

  return useMutation({
    mutationFn: async ({
      notes,
      text,
      noteType,
      userId,
      properties,
    }: {
      notes: ApiNote[];
      text: string;
      noteType: ApiNote['noteType'];
      userId?: string;
      properties?: ApiNote['properties'];
    }) => {
      const [first] = notes;
      const result = await api.featureCollections
        .features(first.featureCollectionId)
        .mergeNotes(first.projectId, first.featureId, {
          noteIds: notes.map((note) => note.id as number),
          text,
          noteType,
          userId,
          properties,
        });
      return result.toJS().data as ApiNote;
    },
    onMutate: async ({notes}) => {
      await queryClient.cancelQueries({queryKey});
      const previousData = queryClient.getQueryData(queryKey);

      const mergedIds = new Set(notes.map((note) => note.id));
      queryClient.setQueryData(queryKey, (old: ApiExploreRun[] | undefined) => {
        if (!old) return old;
        return old.map((run) => ({
          ...run,
          notes: run.notes.map((note) =>
            mergedIds.has(note.id)
              ? {...note, isArchived: true, noteType: NOTE_TYPE_AGENT as ApiNote['noteType']}
              : note
          ),
        }));
      });

      return {previousData};
    },
    onSuccess: (mergedNote) => {
      const runId = mergedNote.properties?.agent_run_id;
      queryClient.setQueryData(queryKey, (old: ApiExploreRun[] | undefined) => {
        if (!old) return old;
        return old.map((run) =>
          run.id === runId ? {...run, notes: [...run.notes, mergedNote]} : run
        );
      });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey});
    },
  });
};

export const useArchiveExploreRun = (featureId: number) => {
  const queryClient = useQueryClient();
  const {removePendingRun} = usePendingAgentRuns();
  const {queryKey} = exploreRunsQueryOptions(featureId);
  return useMutation({
    mutationFn: async ({agentRunId}: {agentRunId: number}) => {
      const response = await api.agent.archiveExploreRun(agentRunId);
      return response.toJS().data as ApiExploreRun;
    },
    onSuccess: (_run, {agentRunId}) => {
      removePendingRun(agentRunId);
      queryClient.invalidateQueries({queryKey});
    },
    onMutate: async ({agentRunId}) => {
      await queryClient.cancelQueries({queryKey});
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: ApiExploreRun[] | undefined) => {
        if (!old) return old;
        return old.filter((run) => run.id !== agentRunId);
      });
      return {previousData};
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey});
    },
  });
};

export const useSubmitRunFeedback = () => {
  return useMutation({
    mutationFn: ({
      agentRunId,
      helpful,
      comment,
    }: {
      agentRunId: number;
      helpful: boolean;
      comment?: string;
    }) => api.agent.submitRunFeedback(agentRunId, helpful, comment),
  });
};

export const useAgentRunLogs = (agentRunId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['agent-run-logs', agentRunId],
    queryFn: async () => {
      const response = await api.agent.getRunLogs(agentRunId);
      return response.toJS().data as {logs: string};
    },
    enabled,
  });
};
